import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Un seul soleil, analytique.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'rings',
  family: 'cosmic',
  danceability: 0.42,
  requiresTier: 0,
  energyLevel: 0.25,
  bpmMin: 50,
  bpmMax: 140,
  movement: 0.25,
  tonality: 0.80,
  enabled: true
};

// RINGS : une traversée lente des anneaux d'une planète géante. Ambient,
// musique spatiale, post-rock, piano, électro douce : preset lounge (R9),
// aucun flash, aucune vignette.
//
// LA SCÈNE EST EN VRAIE 3D, dans le repère de la planète : sphère de rayon 1 à
// l'origine, axe y ; anneaux dans son plan équatorial (y = 0), de 1,25 à 2,30,
// centrés sur elle, plus un voile de poussière très ténu au-delà. La caméra
// est à 4,5 rayons, entre 4° et 24° au-dessus du plan selon le cycle de scène ;
// son CADRAGE place la planète en haut à droite (point d'écran demandé, calculé
// par itération sur la base caméra), le centre de l'écran tombe sur les anneaux
// proches, entre la planète et le premier plan (R19 : le centre de composition
// est le centre de l'écran ; aucun assombrissement en espace écran).
//
// UN SEUL SHADER PLEIN ÉCRAN pour la planète, les anneaux et les étoiles :
// chaque pixel lance son rayon, coupe la sphère (t_s) et le plan (t_r), et
// l'ordre est exact : t_r < t_s, l'anneau est devant et la planète se devine
// à travers (transmission exp(−1,2 d / μ) : 20 à 60 % selon la densité, nulle
// dans l'anneau B) ; t_r > t_s, la planète cache l'anneau. Le shader écrit la
// PROFONDEUR de la planète (gl_FragDepth) : les blocs et la poussière, rendus
// ensuite en géométrie, passent derrière elle par le tampon de profondeur.
//
// LES OMBRES, par rayons vers le soleil :
//   - de la planète sur les anneaux : le rayon depuis le point d'anneau coupe
//     la sphère → ombre nette (bord adouci sur 1 %), un croissant sombre
//     derrière la planète, à l'opposé du soleil ;
//   - des anneaux sur la planète : le rayon depuis le point de planète coupe le
//     plan en Q ; la table d'anneaux en |Q| (filtrée par fwidth comme les
//     anneaux eux-mêmes) donne la transmission : des bandes fines, l'image
//     projetée des anneaux et de leurs divisions ;
//   - les blocs et la poussière font le même test dans leur vertex shader.
//
// LA TABLE D'ANNEAUX : 4 096 entrées (0,00026 R chacune) tirées au démarrage —
// anneau C pâle, B dense, division de Cassini, A avec Encke et Keeler, F, des
// centaines de bandes par bruit multi-échelles, ringlets clairs et lacunes.
// Stockée PRÉFILTRÉE en 13 lignes (moyenne glissante sur 2^k entrées, couleur
// prémultipliée, densité en alpha) ; le shader prend la ligne de fwidth(r) :
// une moyenne exacte de ce que le pixel recouvre au sol, pas de fourmillement.
// RGBA8 : le Pi filtre les octets, pas les flottants.
//
// LA FACE NOCTURNE n'est pas un aplat : elle reçoit la lumière renvoyée par les
// anneaux éclairés (ringshine), douce, venant du plan, où les bandes de
// l'atmosphère se devinent encore.
//
// LES BLOCS : 2 000 éclats de glace (800 en tier 0), un icosaèdre taillé par
// des plans, instancié ; matcap glace (R5), blancs, reflet net vers le soleil,
// scintillement. Poussière fine en 1 px (R7) en plus. Tous dans le plan, avec
// une légère épaisseur, en orbite (vitesse ∝ r^-1,5, la caméra a la sienne :
// le relatif les fait défiler). Tailles : poussière au loin, petits blocs au
// milieu, quelques gros au premier plan (plafonnés à 3,5 % de la hauteur), qui
// tournent et passent près de la caméra avec un flou de profondeur (bord
// fondu par le cercle de confusion). Tout est dans le vertex shader : un temps
// orbital intégré en uniforme, rien n'est téléversé par frame.
//
// LA MUSIQUE : adapt.intensity, lissée sur 3 s, règle la vitesse du temps
// orbital et le scintillement. Toutes les 8 s, un éclat de soleil glisse le long
// d'une bande brillante.

const R0 = 1.25, R1 = 2.30, HAZE_R = 4.8;
const TAB_N = 4096, TAB_LEVELS = 13;
const CAM_D = 4.5;
const FOCUS = 2.6;
const FOV_DEG = 55;
const PLANET_NDC = [0.40, 0.36];           // where the planet's centre sits on screen (top right)
const PERIOD_S = 65;
const GLINT_EVERY_S = 8, GLINT_S = 2.6;
// the sun in the CAMERA-AZIMUTH frame (x toward the camera, y up, -z screen right): in front,
// 32° to the right, 12° above the plane: low, so the rings' shadow hugs the equator on the lit
// face, ABOVE the near rings' crossing (a higher sun puts it under the crossing, hidden), and
// the planet's shadow stretches far across the rings behind it, screen left.
const SUN_LOCAL = new THREE.Vector3(0.83, 0.21, -0.52).normalize();
const OMEGA_K = 0.09, OMEGA_CAM = 0.004;
const TIERS = { 0: { blocks: 800, dust: 1500, msaa: 0 }, 1: { blocks: 2000, dust: 3000, msaa: 4 }, 2: { blocks: 2000, dust: 3000, msaa: 4 } };

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const smooth01 = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

// the camera's elevation over the ring plane along the scene cycle (degrees)
export function altitudeAt(t) {
  if (t < 20) return 4 + 20 * smooth01(t / 20);
  if (t < 30) return 24;
  if (t < 60) return 24 - 20 * smooth01((t - 30) / 30);
  return 4;
}

// --- the ring table ----------------------------------------------------------------
export function ringProfile(seed) {
  const rnd = mulberry(seed);
  const dens = new Float32Array(TAB_N), col = new Float32Array(TAB_N * 3);
  // multi-scale band noise: a value per cell, cells of 10, 24, 56 and 160 entries (0.0026 to 0.04 R)
  const octave = (cell) => { const n = Math.ceil(TAB_N / cell) + 2, v = new Float32Array(n); for (let i = 0; i < n; i++) v[i] = rnd(); return (i) => { const f = i / cell, k = Math.floor(f), t = f - k; return v[k] * (1 - t) + v[k + 1] * t; }; };
  const o1 = octave(10), o2 = octave(24), o3 = octave(56), o4 = octave(160);
  const gauss = (r, c, w) => Math.exp(-Math.pow((r - c) / w, 2));
  const gaps = [1.33, 1.46, 1.48, 1.505, 2.075, 2.095, 2.185, 2.235];
  const gapW = [0.0035, 0.004, 0.0025, 0.002, 0.003, 0.002, 0.0028, 0.0011];
  const lets = [1.31, 1.39, 1.45, 1.48, 2.07, 2.085];
  for (let k = 0; k < 6; k++) { gaps.push(1.57 + rnd() * 0.33); gapW.push(0.0012 + rnd() * 0.0015); }
  for (let i = 0; i < TAB_N; i++) {
    const r = R0 + (i + 0.5) / TAB_N * (R1 - R0);
    let d, c;
    let bands = 0.30 * o1(i) + 0.32 * o2(i) + 0.24 * o3(i) + 0.14 * o4(i);
    bands = smooth01((bands - 0.5) * 1.8 + 0.5);
    if (r < 1.28) { d = 0.03 * (r - R0) / 0.03; c = [0.62, 0.62, 0.64]; }
    else if (r < 1.55) {                                   // C ring: faint, greyish, plateaus
      d = 0.12 + 0.10 * smooth01((r - 1.28) / 0.27) + 0.10 * o4(i) + 0.16 * (bands - 0.5);
      c = [0.66, 0.64, 0.62];
    } else if (r < 1.93) {                                 // B ring: dense, warm, the finest banding
      const core = 0.72 + 0.22 * Math.sin((r - 1.55) / 0.38 * Math.PI);
      d = core * (0.55 + 0.75 * (bands - 0.5)) + 0.08 * (o3(i) - 0.5);
      c = [0.95, 0.86, 0.70];
    } else if (r < 1.99) {                                 // Cassini division
      d = 0.04 + 0.03 * o2(i);
      c = [0.58, 0.58, 0.62];
    } else if (r < 2.25) {                                 // A ring
      d = 0.46 + 0.10 * Math.sin((r - 1.99) * 15) + 0.45 * (bands - 0.5);
      c = [0.84, 0.80, 0.74];
    } else {
      d = 0.008;
      c = [0.7, 0.7, 0.7];
    }
    for (let k = 0; k < gaps.length; k++) d *= 1 - 0.93 * gauss(r, gaps[k], gapW[k]);
    for (let k = 0; k < lets.length; k++) d += 0.25 * gauss(r, lets[k], 0.0022);
    d += 0.45 * gauss(r, 2.285, 0.0018);                   // F ring
    d = Math.min(1, Math.max(0, d));
    const warm = 0.9 + 0.2 * d;
    dens[i] = d;
    col[i * 3] = Math.min(1, c[0] * warm); col[i * 3 + 1] = Math.min(1, c[1] * warm); col[i * 3 + 2] = Math.min(1, c[2] * (0.95 + 0.1 * d));
  }
  return { dens, col };
}
// prefiltered rows: row k = box average over 2^k entries (premultiplied colour, density)
function ringTexture(seed) {
  const { dens, col } = ringProfile(seed);
  const P = [0, 1, 2, 3].map(() => new Float64Array(TAB_N + 1));
  for (let i = 0; i < TAB_N; i++) {
    P[0][i + 1] = P[0][i] + col[i * 3] * dens[i]; P[1][i + 1] = P[1][i] + col[i * 3 + 1] * dens[i];
    P[2][i + 1] = P[2][i] + col[i * 3 + 2] * dens[i]; P[3][i + 1] = P[3][i] + dens[i];
  }
  const data = new Uint8Array(TAB_N * TAB_LEVELS * 4);
  for (let L = 0; L < TAB_LEVELS; L++) {
    const w = Math.pow(2, L);
    for (let i = 0; i < TAB_N; i++) {
      const a = Math.max(0, Math.round(i + 0.5 - w / 2)), b = Math.min(TAB_N, Math.max(a + 1, Math.round(i + 0.5 + w / 2)));
      for (let c = 0; c < 4; c++) data[(L * TAB_N + i) * 4 + c] = Math.round(255 * Math.min(1, (P[c][b] - P[c][a]) / (b - a)));
    }
  }
  const tex = new THREE.DataTexture(data, TAB_N, TAB_LEVELS, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

// the planet's latitude bands: pastel, soft transitions, stored sRGB
function bandTexture(seed) {
  const rnd = mulberry(seed);
  const pal = [[0.95, 0.89, 0.74], [0.86, 0.68, 0.44], [0.90, 0.82, 0.68], [0.70, 0.78, 0.86], [0.93, 0.85, 0.66], [0.80, 0.62, 0.42]];
  const n = 256, data = new Uint8Array(n * 4);
  const edges = [0]; let x = 0;
  while (x < 1) { const lat = Math.abs(x - 0.5) * 2; x += 0.018 + 0.05 * rnd() * (0.6 + lat); edges.push(Math.min(1, x)); }
  const cols = edges.map(() => pal[Math.floor(rnd() * pal.length)]);
  for (let i = 0; i < n; i++) {
    const v = (i + 0.5) / n;
    let k = 0; while (k < edges.length - 2 && edges[k + 1] < v) k++;
    const a = cols[k], b = cols[k + 1] || a;
    const t = smooth01((v - edges[k]) / Math.max(1e-3, edges[k + 1] - edges[k]) * 3 - 2);
    const polar = Math.pow(Math.abs(v - 0.5) * 2, 3);
    for (let c = 0; c < 3; c++) {
      let s = a[c] * (1 - t) + b[c] * t;
      s = s * (1 - 0.35 * polar) + [0.55, 0.65, 0.78][c] * 0.35 * polar;
      data[i * 4 + c] = Math.round(255 * Math.pow(Math.min(1, s), 1 / 2.2));
    }
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, n, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}
function noiseTexture(seed) {
  const rnd = mulberry(seed), n = 256, data = new Uint8Array(n * n * 4);
  for (let i = 0; i < data.length; i++) data[i] = Math.floor(rnd() * 256);
  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

// the ice matcap (R5): 40 % dark tones, two wide soft highlights, pale blue tints, white ice
function iceMatcap() {
  const n = 256;
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return null;
  c.width = c.height = n;
  const g = c.getContext('2d');
  const img = g.createImageData(n, n);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const u = (x + 0.5) / n * 2 - 1, v = 1 - (y + 0.5) / n * 2;
    const r2 = u * u + v * v, o = (y * n + x) * 4;
    if (r2 > 1) { img.data[o + 3] = 255; continue; }
    const z = Math.sqrt(1 - r2);
    const lit = Math.max(0, 0.40 * u + 0.60 * v + 0.55 * z);
    let rr = 0.14 + 0.72 * lit, gg = 0.17 + 0.76 * lit, bb = 0.24 + 0.80 * lit;
    const h1 = Math.exp(-((u - 0.35) ** 2 + (v - 0.45) ** 2) / 0.06), h2 = Math.exp(-((u + 0.45) ** 2 + (v - 0.1) ** 2) / 0.09) * 0.45;
    rr += 0.5 * h1 + 0.3 * h2; gg += 0.52 * h1 + 0.33 * h2; bb += 0.55 * h1 + 0.4 * h2;
    const fr = Math.pow(1 - z, 3) * 0.25;
    rr += fr * 0.6; gg += fr * 0.8; bb += fr;
    img.data[o] = Math.min(255, rr * 255); img.data[o + 1] = Math.min(255, gg * 255); img.data[o + 2] = Math.min(255, bb * 255); img.data[o + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
}

// an irregular chunk: an icosphere with shared vertices, cut by planes, smooth normals (R5)
function rockGeometry(seed) {
  const rnd = mulberry(seed);
  const base = new THREE.IcosahedronGeometry(1, 1);
  const p = base.attributes.position, map = new Map(), verts = [], index = [];
  for (let i = 0; i < p.count; i++) {
    const key = p.getX(i).toFixed(4) + ',' + p.getY(i).toFixed(4) + ',' + p.getZ(i).toFixed(4);
    let k = map.get(key);
    if (k === undefined) { k = verts.length / 3; map.set(key, k); verts.push(p.getX(i), p.getY(i), p.getZ(i)); }
    index.push(k);
  }
  const cuts = []; for (let k = 0; k < 9; k++) cuts.push([new THREE.Vector3(rnd() * 2 - 1, rnd() * 2 - 1, rnd() * 2 - 1).normalize(), 0.55 + 0.35 * rnd()]);
  for (let i = 0; i < verts.length; i += 3) {
    const v = new THREE.Vector3(verts[i], verts[i + 1], verts[i + 2]);
    let s = 1;
    for (const [d, h] of cuts) { const k = v.dot(d); if (k * s > h) s = h / k; }
    s *= 1 + (rnd() - 0.5) * 0.12;
    verts[i] *= s; verts[i + 1] *= s; verts[i + 2] *= s;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  g.setIndex(index);
  g.computeVertexNormals();
  base.dispose();
  return g;
}

// --- GLSL ----------------------------------------------------------------------------
// shared by the blocks and the dust: the orbit, the planet's shadow test
const ORBIT_CHUNK = `
  #define PI 3.14159265
  uniform float uT, uPhiC;
  uniform vec3 uSun;
  // a body at radius r, base azimuth a0 (0..1 over the camera's half circle), Keplerian drift
  // relative to the camera, wrapped so every body stays on the camera's side
  vec3 orbitPos(float r, float a0, float y) {
    float w = ${OMEGA_K.toFixed(3)} * pow(r, -1.5) - ${OMEGA_CAM.toFixed(4)};
    float rel = mod(a0 * PI + w * uT, PI) - PI * 0.5;
    float phi = uPhiC + rel;
    return vec3(r * cos(phi), y, r * sin(phi));
  }
  // 1 in the sun, 0 inside the planet's shadow (soft edge)
  float planetShadow(vec3 P) {
    float bs = dot(P, uSun);
    float dist = sqrt(max(dot(P, P) - bs * bs, 0.0));
    return bs < 0.0 ? smoothstep(0.985, 1.015, dist) : 1.0;
  }
`;

const skyVertexShader = `
  precision highp float;
  varying vec2 vNdc;
  void main() { vNdc = position.xy * 2.0; gl_Position = vec4(position.xy * 2.0, 0.999, 1.0); }
`;
const skyFragmentShader = `
  precision highp float;
  uniform vec3 uCamPos, uCamR, uCamU, uCamF, uSun;
  uniform float uTanH, uAspect, uT, uPhiC, uRot, uNear, uFar;
  uniform sampler2D uRing, uBands, uNoise;
  uniform vec4 uGlint;          // r, phi (relative to the camera's azimuth), amplitude, width in phi
  uniform float uNoFilter;      // debug: the raw table, to show what the prefilter removes
  varying vec2 vNdc;
  #define PI 3.14159265
  #define R0 ${R0.toFixed(2)}
  #define R1 ${R1.toFixed(2)}
  #define KAPPA 1.2
  float vnoise(vec2 p) { return texture2D(uNoise, p / 256.0).r; }
  float fbm(vec2 p) { float s = 0.0, a = 0.5; for (int i = 0; i < 4; i++) { s += a * vnoise(p); p *= 2.03; a *= 0.5; } return s / 0.9375; }
  // the ring table at radius r, box-filtered over the radial width fwr the pixel covers
  vec4 ringAt(float r, float fwr) {
    float u = (r - R0) / (R1 - R0);
    float texels = fwr / (R1 - R0) * ${TAB_N.toFixed(1)};
    float L = clamp(log2(max(texels, 1.0)), 0.0, ${(TAB_LEVELS - 1).toFixed(1)}) * (1.0 - uNoFilter);
    vec4 s = texture2D(uRing, vec2(u, (L + 0.5) / ${TAB_LEVELS.toFixed(1)}));
    // the tenuous outer dust sheet beyond the main rings
    float haze = 0.02 * smoothstep(R1, R1 + 0.10, r) * (1.0 - smoothstep(2.6, ${HAZE_R.toFixed(1)}, r));
    s += vec4(0.55, 0.58, 0.64, 1.0) * haze;
    return s;
  }
  float planetShadow(vec3 P) {
    float bs = dot(P, uSun);
    float dist = sqrt(max(dot(P, P) - bs * bs, 0.0));
    return bs < 0.0 ? smoothstep(0.985, 1.015, dist) : 1.0;
  }
  vec3 planet(vec3 P, vec3 rd, float rq, float fwq) {
    vec3 N = normalize(P);
    float lat = asin(clamp(N.y, -1.0, 1.0)), lon = atan(N.z, N.x);
    float lo = lon + uRot * (1.0 + 0.35 * sin(lat * 9.0));
    float n = fbm(vec2(lo * 9.0, lat * 70.0));
    float latp = lat + 0.035 * (n - 0.5) + 0.012 * (fbm(vec2(lo * 30.0 + n * 3.0, lat * 200.0)) - 0.5);
    vec3 c = pow(texture2D(uBands, vec2(latp / PI + 0.5, 0.5)).rgb, vec3(2.2));
    c *= 0.88 + 0.24 * fbm(vec2(lo * 20.0 + n * 4.0, lat * 140.0));
    for (int k = 0; k < 2; k++) {
      float slat = k == 0 ? -0.38 : 0.52, w = k == 0 ? 0.20 : 0.09, h = k == 0 ? 0.065 : 0.035;
      float slon = (k == 0 ? 0.25 : -0.45) + uRot * (1.0 + 0.35 * sin(slat * 9.0));
      float dl = mod(lo - slon + PI, 2.0 * PI) - PI;
      vec2 q = vec2(dl / w, (lat - slat) / h);
      float e = dot(q, q);
      float ang = atan(q.y, q.x) + 2.5 * exp(-e);
      float sw = fbm(vec2(cos(ang), sin(ang)) * (2.0 + 3.0 * sqrt(e)) + float(k) * 17.0);
      float m = exp(-e * 1.2);
      vec3 sc = k == 0 ? vec3(0.95, 0.70, 0.50) : vec3(0.95, 0.93, 0.88);
      c = mix(c, sc * (0.85 + 0.3 * sw), m * 0.85);
      c *= 1.0 - 0.18 * exp(-pow(sqrt(e) - 1.15, 2.0) * 12.0);
    }
    float ndl = dot(N, uSun);
    float day = smoothstep(-0.08, 0.30, ndl) * (0.25 + 0.75 * max(ndl, 0.0));
    // the rings' shadow: the sun ray from P crosses the plane at radius rq (computed by the
    // caller for every pixel, so fwidth is valid): the table's transmission, band by band
    float t = -P.y / uSun.y;
    if (t > 0.0 && rq > R0 && rq < R1 + 0.5) {
      float d = ringAt(rq, fwq).a;
      day *= exp(-KAPPA * d / max(abs(uSun.y), 0.05));
    }
    float mu = max(dot(N, -rd), 0.0);
    // ringshine on the night side: the lit rings, seen from the planet, glow from the plane
    float toward = max(0.0, 1.0 - abs(N.y) * 1.25);
    float rs = 0.040 * toward * toward * (N.y > 0.0 ? 1.0 : 0.4) * (0.35 + 0.65 * smoothstep(-0.6, 0.2, dot(normalize(vec3(N.x, 0.0, N.z)), uSun)));
    vec3 col = c * (day * 0.45 + 0.002) + c * vec3(0.85, 0.92, 1.0) * rs;
    col *= 0.55 + 0.45 * pow(mu, 0.4);
    col = mix(col, col.bbb * vec3(0.55, 0.7, 1.0), pow(1.0 - mu, 3.0) * 0.45);
    return col;
  }
  // a fine star field (R7): cells of about 2 px, one star in 1.5 % of them
  vec3 space(vec3 rd) {
    vec2 g = vec2(atan(rd.z, rd.x), asin(clamp(rd.y, -1.0, 1.0))) * 420.0;
    vec2 cell = floor(g), f = fract(g) - 0.5;
    float h = fract(sin(dot(cell, vec2(127.1, 311.7))) * 43758.5453);
    float h2 = fract(sin(dot(cell, vec2(269.5, 183.3))) * 43758.5453);
    float on = step(0.98, h);
    float b = (h - 0.98) / 0.02;
    float s = on * smoothstep(0.55, 0.1, length(f - (vec2(h2, fract(h2 * 7.0)) - 0.5) * 0.4)) * (0.15 + 0.85 * b * b);
    vec3 tint = mix(vec3(0.75, 0.82, 1.0), vec3(1.0, 0.9, 0.75), h2);
    return tint * s * 0.9;
  }
  void main() {
    vec3 ro = uCamPos;
    vec3 rd = normalize(uCamF + vNdc.x * uTanH * uAspect * uCamR + vNdc.y * uTanH * uCamU);
    // the sphere
    float b = dot(ro, rd), c = dot(ro, ro) - 1.0, disc = b * b - c;
    float ts = disc > 0.0 ? -b - sqrt(disc) : -1.0;
    // the ring plane (the camera stays above it)
    float tr = rd.y < -1e-5 ? -ro.y / rd.y : -1.0;
    vec3 Q = ro + rd * (tr > 0.0 ? tr : 1e3);
    float r = length(Q.xz);
    float fwr = fwidth(r);
    // the ring-shadow lookup point for the planet pixel, computed for every pixel (derivatives)
    vec3 P = ro + rd * max(ts, 0.0);
    vec3 Qs = P + uSun * max(-P.y / uSun.y, 0.0);
    float rq = length(Qs.xz);
    float fwq = fwidth(rq);
    // the limb: coverage from the discriminant's pixel footprint (a shader edge has no MSAA)
    float edge = smoothstep(0.0, 1.5 * fwidth(disc), disc);
    vec3 col = space(rd);
    // the ring, computed once; composited behind the planet first, in front of it last
    bool ring = tr > 0.0 && r > R0 && r < ${HAZE_R.toFixed(1)};
    vec3 lit = vec3(0.0); float alpha = 0.0;
    if (ring) {
      vec4 s = ringAt(r, fwr);
      float d = s.a;
      vec3 rc = s.rgb / max(d, 1e-3);
      float fine = 1.0 + 0.18 * (fract(sin(floor(r * 30000.0) * 91.7) * 4375.85) - 0.5) * (1.0 - smoothstep(0.00004, 0.00012, fwr));
      d = clamp(d * fine, 0.0, 1.0);
      float mu = max(-rd.y, 0.02);
      alpha = 1.0 - exp(-KAPPA * d / mu);
      float sh = planetShadow(Q);
      float vs = dot(rd, uSun);
      float ph = 0.55 + 0.9 * pow(max(vs, 0.0), 3.0) + 0.3 * pow(max(-vs, 0.0), 8.0);
      lit = rc * 0.50 * ph * (0.04 + 0.96 * sh);
      float phi = atan(Q.z, Q.x) - uPhiC;
      float gl = uGlint.z * exp(-pow((r - uGlint.x) / 0.0012, 2.0)) * exp(-pow((phi - uGlint.y) / uGlint.w, 2.0)) * sh;
      lit += vec3(1.0, 0.95, 0.85) * gl * 1.6 * (1.0 - smoothstep(0.002, 0.01, fwr));
    }
    if (ring && (ts < 0.0 || tr > ts)) col = mix(col, lit, alpha);
    float depth = 1.0;
    if (edge > 0.0) {
      col = mix(col, planet(P, rd, rq, fwq), edge);
      float zv = max(ts, 1e-3) * dot(rd, uCamF);
      float ndc = (uFar + uNear) / (uFar - uNear) - 2.0 * uFar * uNear / ((uFar - uNear) * zv);
      if (edge > 0.5) depth = ndc * 0.5 + 0.5;
    }
    if (ring && ts > 0.0 && tr < ts) col = mix(col, lit, alpha);
    gl_FragDepthEXT = depth;
    gl_FragColor = vec4(pow(max(col, 0.0), vec3(1.0 / 2.2)), 1.0);
  }
`;

const rockVertexShader = `
  precision highp float;
  ${ORBIT_CHUNK}
  attribute vec4 aI;     // r, azimuth 0..1, size (R), seed
  attribute vec4 aS;     // non-uniform scale xyz, height offset
  uniform float uTanH, uPxH, uFocus;
  varying vec3 vNw, vNv, vVw;
  varying float vSeed, vCoc, vSh;
  mat3 rotAxis(vec3 a, float t) {
    float c = cos(t), s = sin(t), k = 1.0 - c;
    return mat3(c + a.x * a.x * k, a.y * a.x * k + a.z * s, a.z * a.x * k - a.y * s,
                a.x * a.y * k - a.z * s, c + a.y * a.y * k, a.z * a.y * k + a.x * s,
                a.x * a.z * k + a.y * s, a.y * a.z * k - a.x * s, c + a.z * a.z * k);
  }
  void main() {
    vec3 C = orbitPos(aI.x, aI.y, aS.w);
    float dist = max(length(cameraPosition - C), 1e-3);
    // never above 3.5 % of the height, never under 1.5 px
    float size = clamp(aI.z, 1.5 * dist / uPxH, 0.07 * uTanH * dist);
    // the circle of confusion: the nearer the block, the softer and the larger its edge
    float coc = clamp(0.45 * abs(1.0 / dist - 1.0 / uFocus), 0.0, 1.0) * step(dist, uFocus);
    size *= 1.0 + 0.7 * coc;
    vec3 axis = normalize(vec3(fract(aI.w * 13.1) - 0.5, fract(aI.w * 7.3) - 0.5, fract(aI.w * 3.7) - 0.5) + 1e-3);
    mat3 R = rotAxis(axis, aI.w * 40.0 + uT * (0.08 + 0.25 * fract(aI.w * 5.9)));
    vec3 p = R * (position * aS.xyz) * size;
    vec3 w = C + p;
    vNw = normalize(R * (normal / aS.xyz));
    vNv = normalize(mat3(viewMatrix) * vNw);
    vVw = normalize(cameraPosition - w);
    vSeed = aI.w; vCoc = coc; vSh = planetShadow(C);
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const rockFragmentShader = `
  precision highp float;
  uniform sampler2D uMatcap;
  uniform vec3 uSun;
  uniform float uT, uSparkle;
  varying vec3 vNw, vNv, vVw;
  varying float vSeed, vCoc, vSh;
  void main() {
    vec3 n = normalize(vNw), nv = normalize(vNv), V = normalize(vVw);
    vec3 mc = pow(texture2D(uMatcap, nv.xy * 0.49 + 0.5).rgb, vec3(2.2));
    float lam = max(dot(n, uSun), 0.0);
    // white ice: the sun's lambert, a little ringshine and sky from below, the planet's shadow
    float emb = vSeed < 0.02 ? 0.85 : 1.0;                      // embedded in the rings: a touch dustier
    vec3 col = vec3(0.92, 0.95, 1.0) * mc * (0.10 + 0.95 * lam * vSh) * emb;
    // the glint: a facet that mirrors the sun to the eye (jittered normal = crystal facets)
    vec3 j = vec3(fract(sin(dot(floor(gl_FragCoord.xy * 0.5), vec2(12.9898, 78.233)) + vSeed * 91.0) * 43758.5) - 0.5,
                  fract(sin(dot(floor(gl_FragCoord.xy * 0.5), vec2(39.346, 11.135)) + vSeed * 37.0) * 43758.5) - 0.5, 0.0);
    vec3 nj = normalize(n + 0.14 * j);
    float g = pow(max(dot(reflect(-V, nj), uSun), 0.0), 60.0);
    float tw = pow(0.5 + 0.5 * sin(uT * (0.9 + 2.1 * fract(vSeed * 17.0)) + vSeed * 60.0), 6.0);
    col += vec3(1.0, 0.97, 0.9) * g * uSparkle * (0.2 + 0.8 * tw) * 2.5 * vSh;
    // defocus: the edge fades with the circle of confusion
    float a = pow(clamp(dot(nv, vec3(0.0, 0.0, 1.0)), 0.0, 1.0), 8.0 * vCoc);
    gl_FragColor = vec4(pow(max(col, 0.0), vec3(1.0 / 2.2)), a);
  }
`;

const dustVertexShader = `
  precision highp float;
  ${ORBIT_CHUNK}
  attribute vec4 aD;     // r, azimuth 0..1, height offset, seed
  uniform float uPx;
  varying vec3 vCol;
  void main() {
    vec3 C = orbitPos(aD.x, aD.y, aD.z);
    gl_Position = projectionMatrix * viewMatrix * vec4(C, 1.0);
    gl_PointSize = max(1.0, uPx);
    float sh = planetShadow(C);
    vCol = vec3(0.85, 0.9, 1.0) * (0.05 + 0.38 * sh) * (0.6 + 0.4 * fract(aD.w * 9.0));
  }
`;
const dustFragmentShader = `
  precision mediump float;
  varying vec3 vCol;
  void main() {
    vec2 p = gl_PointCoord * 2.0 - 1.0;
    if (dot(p, p) > 1.0) discard;
    gl_FragColor = vec4(pow(vCol, vec3(1.0 / 2.2)), 1.0);
  }
`;

const displayVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// no screen-space darkening (the correction brief): a uniform gain only (R10)
const displayFragmentShader = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uGain;
  varying vec2 vUv;
  void main() {
    vec3 col = pow(texture2D(uTex, vUv).rgb, vec3(2.2)) * uGain;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class RingsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const st = parseFloat(urlParam('ringst'));
    this._startT = Number.isFinite(st) && st >= 0 ? st : 0;
    const al = parseFloat(urlParam('ringsalt'));
    this._forceAlt = Number.isFinite(al) ? Math.max(0.5, Math.min(60, al)) : null;
    this._adapt = new Adapt();
    this._post = { bloomStrength: 0.2, bloomThreshold: 0.85, vignette: 0 };
    if (urlParam('ringsprobe') === '1' && typeof window !== 'undefined') window.__rings = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.02, 40);
    this._private = new THREE.Scene();
    this._tanH = Math.tan(FOV_DEG / 2 * Math.PI / 180);

    const shared = { uT: { value: 0 }, uPhiC: { value: 0 }, uSun: { value: new THREE.Vector3() } };
    this._shared = shared;
    this._skyU = Object.assign({
      uCamPos: { value: new THREE.Vector3() }, uCamR: { value: new THREE.Vector3() }, uCamU: { value: new THREE.Vector3() }, uCamF: { value: new THREE.Vector3() },
      uTanH: { value: this._tanH }, uAspect: { value: 1.78 }, uRot: { value: 0 }, uNear: { value: this._cam.near }, uFar: { value: this._cam.far },
      uRing: { value: ringTexture(2027) }, uBands: { value: bandTexture(77) }, uNoise: { value: noiseTexture(31) },
      uGlint: { value: new THREE.Vector4(1.8, 0, 0, 0.12) }, uNoFilter: { value: urlParam('ringsnofilter') === '1' ? 1 : 0 }
    }, shared);
    this._sky = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._skyU, vertexShader: skyVertexShader, fragmentShader: skyFragmentShader,
      depthTest: true, depthFunc: THREE.AlwaysDepth, depthWrite: true, extensions: { fragDepth: true, derivatives: true }
    }));
    this._sky.frustumCulled = false; this._sky.renderOrder = 0;
    this._private.add(this._sky);

    // the dust: 1 px points in the plane, mostly in the main rings
    const nd = this._T.dust, rnd = mulberry(4401);
    const aD = new Float32Array(nd * 4);
    for (let i = 0; i < nd; i++) {
      const u = rnd();
      aD[i * 4] = u < 0.7 ? R0 + (R1 - R0) * rnd() : R1 + (3.8 - R1) * Math.pow(rnd(), 0.7);
      aD[i * 4 + 1] = rnd(); aD[i * 4 + 2] = (rnd() - 0.5) * 0.004; aD[i * 4 + 3] = rnd();
    }
    const gd = new THREE.BufferGeometry();
    gd.setAttribute('position', new THREE.BufferAttribute(new Float32Array(nd * 3), 3));
    gd.setAttribute('aD', new THREE.BufferAttribute(aD, 4));
    this._dustU = Object.assign({ uPx: { value: 1 } }, shared);
    this._dust = new THREE.Points(gd, new THREE.ShaderMaterial({ uniforms: this._dustU, vertexShader: dustVertexShader, fragmentShader: dustFragmentShader, depthTest: true, depthWrite: false }));
    this._dust.frustumCulled = false; this._dust.renderOrder = 1;
    this._private.add(this._dust);

    // the ice blocks: one instanced draw call, static per-instance attributes
    const n = this._T.blocks;
    const rock = rockGeometry(9);
    const g = new THREE.InstancedBufferGeometry();
    g.index = rock.index; g.setAttribute('position', rock.attributes.position); g.setAttribute('normal', rock.attributes.normal);
    const aI = new Float32Array(n * 4), aS = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      // radius: 12 % inside the main rings (tiny, embedded), 88 % scattered outward to the camera
      const u = rnd();
      const r = u < 0.12 ? R0 + (R1 - R0) * rnd() : R1 + (5.0 - R1) * Math.sqrt(rnd());
      // size grows toward the camera: fine far away, small mid-way, a few big ones in the foreground
      const s = rnd(), near = Math.max(0, (r - 2.3) / 2.7);
      let size = 0.0025 + 0.004 * s;
      if (r > R1) size = (0.003 + 0.006 * s) * (0.6 + 1.2 * near);
      if (r > 3.6 && s > 0.9) size = 0.018 + 0.016 * rnd();
      aI[i * 4] = r; aI[i * 4 + 1] = rnd(); aI[i * 4 + 2] = size; aI[i * 4 + 3] = r < R1 ? rnd() * 0.02 : 0.02 + rnd() * 0.98;
      aS[i * 4] = 0.7 + 0.6 * rnd(); aS[i * 4 + 1] = 0.55 + 0.5 * rnd(); aS[i * 4 + 2] = 0.7 + 0.6 * rnd(); aS[i * 4 + 3] = (rnd() - 0.5) * 0.02;
    }
    g.setAttribute('aI', new THREE.InstancedBufferAttribute(aI, 4));
    g.setAttribute('aS', new THREE.InstancedBufferAttribute(aS, 4));
    g.instanceCount = n;
    this._rockGeom = rock;
    this._rockU = Object.assign({ uTanH: { value: this._tanH }, uPxH: { value: 1000 }, uFocus: { value: FOCUS }, uSparkle: { value: 0.5 }, uMatcap: { value: iceMatcap() } }, shared);
    this._rocks = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._rockU, vertexShader: rockVertexShader, fragmentShader: rockFragmentShader, transparent: true, depthTest: true, depthWrite: true }));
    this._rocks.frustumCulled = false; this._rocks.renderOrder = 2;
    this._private.add(this._rocks);

    this._target = null;
    this._dispU = { uTex: { value: null }, uGain: { value: 1 } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: displayVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._clock = new SceneClock({ period: PERIOD_S, start: this._startT / PERIOD_S % 1 });
    this._intensity = 0.3;
    this._orbitT = 0; this._rot = 0;
    this._placeCamera(1.78);
  }

  // the camera on its orbit at the cycle's elevation; its basis is solved so the planet's centre
  // lands on PLANET_NDC (a fixed point: F = normalize(d - px·a·R - py·tanH·U), R and U from F)
  _placeCamera(aspect) {
    const fovV = Math.max(FOV_DEG, 2 * Math.atan(Math.tan(25 * Math.PI / 180) / aspect) * 180 / Math.PI);
    if (Math.abs(this._cam.fov - fovV) > 1e-3) { this._cam.fov = fovV; this._cam.updateProjectionMatrix(); }
    const tanH = this._tanH = Math.tan(fovV / 2 * Math.PI / 180);
    const altDeg = this._forceAlt != null ? this._forceAlt : altitudeAt(this._clock.t);
    this._alt = altDeg;
    const el = altDeg * Math.PI / 180, phiC = OMEGA_CAM * this._orbitT;
    const cam = this._cam;
    cam.position.set(CAM_D * Math.cos(el) * Math.cos(phiC), CAM_D * Math.sin(el), CAM_D * Math.cos(el) * Math.sin(phiC));
    const d = cam.position.clone().negate().normalize();
    const up = _up, a = tanH * aspect;
    // portrait: the planet a little more to the left, the rings need the width
    const px = aspect < 1 ? 0.30 : PLANET_NDC[0], py = aspect < 1 ? 0.42 : PLANET_NDC[1];
    const F = d.clone(), R = new THREE.Vector3(), U = new THREE.Vector3();
    for (let it = 0; it < 6; it++) {
      R.crossVectors(F, up).normalize(); U.crossVectors(R, F);
      F.copy(d).addScaledVector(R, -px * a).addScaledVector(U, -py * tanH).normalize();
    }
    R.crossVectors(F, up).normalize(); U.crossVectors(R, F);
    cam.up.copy(up);
    cam.lookAt(_tmp.copy(cam.position).add(F));
    cam.updateMatrixWorld();
    const S = this._skyU;
    S.uCamPos.value.copy(cam.position); S.uCamF.value.copy(F); S.uCamR.value.copy(R); S.uCamU.value.copy(U);
    S.uAspect.value = aspect; S.uTanH.value = tanH;
    this._rockU.uTanH.value = tanH;
    // the sun rides with the camera's azimuth: the lighting stays composed over long tracks
    this._shared.uSun.value.set(SUN_LOCAL.x * Math.cos(phiC) - SUN_LOCAL.z * Math.sin(phiC), SUN_LOCAL.y, SUN_LOCAL.x * Math.sin(phiC) + SUN_LOCAL.z * Math.cos(phiC));
    this._shared.uPhiC.value = phiC;
    // R10 (lounge, 0.35)
    this._dispU.uGain.value = aspect < 1 ? 0.84 : 0.9;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (!playing) return;                                    // frozen, never black
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 3);
    const I = this._intensity, t = this._clock.elapsed;
    // the glide: an orbital time whose rate the music sets, integrated
    this._orbitT += dt * (0.6 + 0.9 * I);
    this._rot += dt * 0.006 * (0.8 + 0.4 * I);
    this._shared.uT.value = this._orbitT;
    this._rockU.uSparkle.value = 0.35 + 0.9 * I;
    this._skyU.uRot.value = this._rot;
    // the sun glint: every 8 s, 2.6 s along one bright band
    const k = Math.floor(t / GLINT_EVERY_S), g = t - k * GLINT_EVERY_S;
    const radii = [1.62, 1.70, 1.78, 1.86, 1.91, 2.08, 2.15, 2.24];
    const p = g / GLINT_S;
    this._skyU.uGlint.value.set(radii[(k * 5) % radii.length], 0.9 - 1.8 * smooth01(p), p < 1 ? Math.sin(Math.PI * Math.min(p, 1)) * 0.9 : 0, 0.12);
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const cam = this._cam;
    if (Math.abs(cam.aspect - w / h) > 1e-4) { cam.aspect = w / h; cam.updateProjectionMatrix(); }
    this._placeCamera(w / h);
    this._rockU.uPxH.value = h / (2 * this._tanH);
    this._dustU.uPx.value = Math.max(1, h / 1080);
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      const msaa = renderer.capabilities.isWebGL2 ? this._T.msaa : 0;
      this._target = new THREE.WebGLRenderTarget(w, h, { type: THREE.UnsignedByteType, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, samples: msaa });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._target);
    renderer.clear(true, true, false);
    renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._target.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display, this._sky, this._rocks, this._dust]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._rockGeom) this._rockGeom.dispose();
    for (const k of ['uRing', 'uBands', 'uNoise']) if (this._skyU && this._skyU[k].value) this._skyU[k].value.dispose();
    if (this._rockU && this._rockU.uMatcap.value) this._rockU.uMatcap.value.dispose();
    if (this._target) this._target.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__rings === this) window.__rings = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _up = new THREE.Vector3(0, 1, 0);
const _tmp = new THREE.Vector3();
